<template>
  <div ref="searchInputContainer" class="search-container">
    <div
      class="search-header-container"
      :style="{
        '--searchInputLargeScreenWidth': searchInputLargeScreenWidth,
        '--searchInputMiddleScreenWidth': searchInputMiddleScreenWidth,
      }"
    >
      <a-input
        ref="refSearchInput"
        v-model:value="keyWord"
        type="text"
        :placeholder="$t('owork_base.search')"
        class="search-input"
        @press-enter="search(keyWord)"
      >
        <template #prefix>
          <img src="@assets/svgs/icon-search.svg" alt="" class="icon-search" />
          <img src="@assets/h5/svgs/icon-search-input.svg" alt="" class="h5-icon-search" />
        </template>
        <template #suffix>
          <span v-show="keyWord.length > 0" alt="" class="icon-search icon-close" @click="clearSearchValue"></span>
        </template>
      </a-input>
      <span class="search-cancel-btn" @click="exitSearch">{{ $t('owork_base.cancel') }}</span>
    </div>
    <Transition>
      <SearchResultsComponent
        v-if="hasSearched"
        class="search-results"
        @open-search-item-folder="openSearchItemFolder"
      />
    </Transition>
  </div>
</template>
<script lang="ts" setup>
  import { ref, nextTick, watch, onMounted, computed } from 'vue'
  import SearchResultsComponent from './SearchResultsComponent.vue'
  import { INSTANCE as SearchManager } from '@core/search/SearchManager'
  import { debounce } from 'lodash'
  import { TimeConstants } from '@core/Constants'
  import { useSharedSearchInputActive } from '@/core/compositions/SearchInputActive'
  import { onClickOutside } from '@vueuse/core'
  import { useSharedActiveMainHeaderItem } from '@/core/compositions/ActiveMainHeaderItem'

  const hasSearched = ref<boolean>(false)
  const keyWord = ref<string>('')
  const { avatarNameContainerWidthGap } = useSharedActiveMainHeaderItem()
  const searchInputMiddleScreenWidth = computed(() => avatarNameContainerWidthGap.value + 388 + 'px')
  const searchInputLargeScreenWidth = computed(() => avatarNameContainerWidthGap.value + 348 + 'px')

  watch(
    () => keyWord.value,
    (newValue) => {
      if (newValue === '' || newValue) search(newValue)
    }
  )

  const search = debounce(async (value: string) => {
    hasSearched.value = true

    if (value === '') {
      hasSearched.value = false
      await SearchManager.clearSearchResult()

      return
    }
    await SearchManager.initSearch(value)
    await SearchManager.fetchSearchList()
  }, TimeConstants.DEFAULT_DEBOUNCING_IN_MS)

  let isShow = useSharedSearchInputActive()

  const toggleVisible = async () => {
    isShow.value = !isShow.value

    if (isShow.value) {
      nextTick(() => focusSearchInput())
    } else {
      keyWord.value = ''
      await SearchManager.clearSearchResult()
    }
  }

  const openSearchItemFolder = () => {
    if (isShow.value) toggleVisible()
  }

  const clearSearchValue = async () => {
    hasSearched.value = false
    keyWord.value = ''
    focusSearchInput()

    await SearchManager.clearSearchResult()
  }
  const refSearchInput = ref()

  const focusSearchInput = () => {
    refSearchInput.value.focus()
  }

  const exitSearch = () => {
    toggleVisible()
  }

  const searchInputContainer = ref<HTMLDivElement>()

  onMounted(() => {
    onClickOutside(searchInputContainer, async () => {
      if (isShow.value) {
        await toggleVisible()
      }
    })
  })

  defineExpose({ toggleVisible })
</script>
<style scoped lang="stylus">

  .search-container
    z-index $zIndexTop
    position absolute
    top 0
    right 0
    :deep(.ant-input-affix-wrapper)
      border-radius 18px
      padding 6px 11px
    :deep(.ant-input-prefix)
      margin-right 8px
    :deep(.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover)
      border-color active_color
    :deep(.ant-input-affix-wrapper-focused)
      border-color active_color
      box-shadow none
      input::placeholder
        color transparent
    .search-input
      width var(--searchInputMiddleScreenWidth)
      box-sizing border-box
      color default_color
      font-size 14px

      caret-color active_color !important
      .icon-search
        rect(20px)
        display inline-block
      .icon-close
        cursor pointer
        background url('@assets/svgs/icon-search-close-default.svg')
        background-size 20px
        &:hover
          background-image url('@assets/svgs/icon-search-close-hover.svg')
    .search-results
      position absolute
      top 31px
      left 0

  @import '../styles/h5Adaptive/SearchInputComponent.styl'
  @media screen and (min-width large_screen_width_threshold )
    .search-container
      .search-input
        width var(--searchInputLargeScreenWidth)
</style>
